import {
  FicCompositionStatus,
  FlattenedTableRow,
  Indicator,
} from 'src/types/wallet';

import { resolveFicPlPercentage } from './ficPl';

type IndicatorsRowOperation = {
  name: string | null;
  cnpj: string;
  slug: string | null;
  hashId: string | null;
  market: { name: string | null } | null;
};

type IndicatorsRowSource = {
  walletFundId: number;
  operation: IndicatorsRowOperation;
  indicators: Indicator[];
  compositionStatus?: FicCompositionStatus;
  childrenWalletFunds?: IndicatorsRowSource[];
};

type BuildFallbackIndicatorsRowInput = {
  walletFundId: number;
  operation: IndicatorsRowOperation;
  compositionStatus?: FicCompositionStatus;
  isFicChild?: boolean;
  isReferenceOnly?: boolean;
};

function buildRowOperation(operation: IndicatorsRowOperation) {
  return {
    market: operation.market?.name ?? '',
    hashId: operation.hashId ?? '',
    slug: operation.slug ?? '',
    name: operation.name ?? 'Fundo sem nome',
    cnpj: operation.cnpj,
  };
}

function isReferenceOnlyFicChild(
  isFollowedFunds: boolean,
  isFicChild: boolean,
  compositionStatus?: FicCompositionStatus,
  walletFundId?: number,
) {
  return (
    isFollowedFunds &&
    isFicChild &&
    (compositionStatus === 'removed' ||
      (compositionStatus === 'added' && !walletFundId))
  );
}

function buildUnavailableIndicatorDecorations(isReferenceOnly: boolean) {
  if (isReferenceOnly) return {};

  return {
    dc_status: 'neutral',
    recompras_status: 'neutral',
    substituicoes_status: 'neutral',
    atrasos_status: 'neutral',
    pdd_status: 'neutral',
    caixa_status: 'neutral',
    pl_status: 'neutral',
    pl_cotas_status: 'neutral',
    amort_total_status: 'neutral',
    amort_pct_status: 'neutral',
    delta_dc_pct_status: 'neutral',
    delta_recompras_pct_status: 'neutral',
    delta_substituicoes_pct_status: 'neutral',
    delta_atrasos_pct_status: 'neutral',
    delta_pdd_pct_status: 'neutral',
    pct_atraso_sobre_dc_status: 'neutral',
    pct_caixa_sobre_pl_status: 'neutral',
    pdd_sobre_dc_status: 'neutral',
    recompra_sobre_dc_status: 'neutral',
    pct_pdd_sobre_atrasos_status: 'neutral',
    dc_alert: 'none',
    recompras_alert: 'none',
    substituicoes_alert: 'none',
    atrasos_alert: 'none',
    pdd_alert: 'none',
    caixa_alert: 'none',
    pl_alert: 'none',
    pl_cotas_alert: 'none',
    amort_total_alert: 'none',
    amort_pct_alert: 'none',
    delta_dc_pct_alert: 'none',
    delta_recompras_pct_alert: 'none',
    delta_substituicoes_pct_alert: 'none',
    delta_atrasos_pct_alert: 'none',
    delta_pdd_pct_alert: 'none',
    pct_atraso_sobre_dc_alert: 'none',
    pct_caixa_sobre_pl_alert: 'none',
    pdd_sobre_dc_alert: 'none',
    recompra_sobre_dc_alert: 'none',
    pct_pdd_sobre_atrasos_alert: 'none',
  };
}

function buildIndicatorDecorations(
  indicator: Indicator,
  isReferenceOnly: boolean,
) {
  if (isReferenceOnly) return {};

  return {
    dc_status: indicator.dc_status,
    recompras_status: indicator.recompras_status,
    substituicoes_status: indicator.substituicoes_status,
    atrasos_status: indicator.atrasos_status,
    pdd_status: indicator.pdd_status,
    caixa_status: indicator.caixa_status,
    pl_status: indicator.pl_status,
    pl_cotas_status: indicator.pl_cotas_status,
    amort_total_status: indicator.amort_total_status,
    amort_pct_status: indicator.amort_pct_status,
    delta_dc_pct_status: indicator.delta_dc_pct_status,
    delta_recompras_pct_status: indicator.delta_recompras_pct_status,
    delta_substituicoes_pct_status: indicator.delta_substituicoes_pct_status,
    delta_atrasos_pct_status: indicator.delta_atrasos_pct_status,
    delta_pdd_pct_status: indicator.delta_pdd_pct_status,
    pct_atraso_sobre_dc_status: indicator.pct_atraso_sobre_dc_status,
    pct_caixa_sobre_pl_status: indicator.pct_caixa_sobre_pl_status,
    pdd_sobre_dc_status: indicator.pdd_sobre_dc_status,
    recompra_sobre_dc_status: indicator.recompra_sobre_dc_status,
    pct_pdd_sobre_atrasos_status: indicator.pct_pdd_sobre_atrasos_status,
    dc_alert: indicator.dc_alert,
    recompras_alert: indicator.recompras_alert,
    substituicoes_alert: indicator.substituicoes_alert,
    atrasos_alert: indicator.atrasos_alert,
    pdd_alert: indicator.pdd_alert,
    caixa_alert: indicator.caixa_alert,
    pl_alert: indicator.pl_alert,
    pl_cotas_alert: indicator.pl_cotas_alert,
    amort_total_alert: indicator.amort_total_alert,
    amort_pct_alert: indicator.amort_pct_alert,
    delta_dc_pct_alert: indicator.delta_dc_pct_alert,
    delta_recompras_pct_alert: indicator.delta_recompras_pct_alert,
    delta_substituicoes_pct_alert: indicator.delta_substituicoes_pct_alert,
    delta_atrasos_pct_alert: indicator.delta_atrasos_pct_alert,
    delta_pdd_pct_alert: indicator.delta_pdd_pct_alert,
    pct_atraso_sobre_dc_alert: indicator.pct_atraso_sobre_dc_alert,
    pct_caixa_sobre_pl_alert: indicator.pct_caixa_sobre_pl_alert,
    pdd_sobre_dc_alert: indicator.pdd_sobre_dc_alert,
    recompra_sobre_dc_alert: indicator.recompra_sobre_dc_alert,
    pct_pdd_sobre_atrasos_alert: indicator.pct_pdd_sobre_atrasos_alert,
  };
}

function getFicChildParticipation(item: IndicatorsRowSource): number | null {
  const indicator = item.indicators[0];
  if (!indicator) return null;

  const percentage = resolveFicPlPercentage(
    indicator.pl_percentage,
    indicator.vl_merc_pos_final,
    indicator.vl_patrim_liq,
  );

  return percentage === 'N/D' ? null : percentage;
}

function compareFicChildrenByParticipation(
  first: IndicatorsRowSource,
  second: IndicatorsRowSource,
): number {
  const firstPercentage = getFicChildParticipation(first);
  const secondPercentage = getFicChildParticipation(second);

  if (firstPercentage === null) return secondPercentage === null ? 0 : 1;
  if (secondPercentage === null) return -1;
  return secondPercentage - firstPercentage;
}

export function buildFallbackIndicatorsRow({
  walletFundId,
  operation,
  compositionStatus,
  isFicChild = false,
  isReferenceOnly = false,
}: BuildFallbackIndicatorsRowInput): FlattenedTableRow {
  const isRemovedFicChild =
    isReferenceOnly && isFicChild && compositionStatus === 'removed';
  const isUnavailable = isRemovedFicChild;
  const unavailableValue = isUnavailable ? null : 'N/D';

  return {
    walletFundId,
    operationName: operation.name ?? 'Fundo sem nome',
    operation: buildRowOperation(operation),
    ...(isFicChild && {
      ficCompositionStatus: compositionStatus,
      isFicChild: true,
      isFicCompositionReferenceOnly: isReferenceOnly,
    }),
    dc: unavailableValue,
    recompras: isUnavailable ? null : 0,
    substituicoes: isUnavailable ? null : 0,
    atrasos: unavailableValue,
    pdd: unavailableValue,
    caixa: unavailableValue,
    pl: unavailableValue,
    pl_cotas: unavailableValue,
    amort_total: isUnavailable ? null : 0,
    amort_pct: isUnavailable ? null : 0,
    delta_dc_pct: unavailableValue,
    delta_recompras_pct: unavailableValue,
    delta_substituicoes_pct: unavailableValue,
    delta_atrasos_pct: unavailableValue,
    delta_pdd_pct: unavailableValue,
    pct_atraso_sobre_dc: unavailableValue,
    pct_caixa_sobre_pl: unavailableValue,
    pdd_sobre_dc: unavailableValue,
    recompra_sobre_dc: unavailableValue,
    pct_pdd_sobre_atrasos: unavailableValue,
    ...buildUnavailableIndicatorDecorations(isReferenceOnly),
  };
}

export function buildIndicatorsTableRow(
  item: IndicatorsRowSource,
  isFicChild = false,
  isFollowedFunds = false,
): FlattenedTableRow {
  const shouldUseFicComposition = isFollowedFunds && isFicChild;
  const isReferenceOnly = isReferenceOnlyFicChild(
    isFollowedFunds,
    isFicChild,
    item.compositionStatus,
    item.walletFundId,
  );
  const indicator =
    isReferenceOnly && item.compositionStatus === 'removed'
      ? undefined
      : item.indicators[0];

  const row: FlattenedTableRow = indicator
    ? {
        walletFundId: item.walletFundId,
        operationName: item.operation.name ?? 'Fundo sem nome',
        operation: buildRowOperation(item.operation),
        ...(shouldUseFicComposition && {
          ficCompositionStatus: item.compositionStatus,
          isFicChild: true,
          isFicCompositionReferenceOnly: isReferenceOnly,
        }),
        dc: indicator.dc,
        recompras: indicator.recompras,
        substituicoes: indicator.substituicoes,
        atrasos: indicator.atrasos,
        pdd: indicator.pdd,
        caixa: indicator.caixa,
        pl: indicator.pl,
        pl_cotas: indicator.pl_cotas,
        amort_total: indicator.amort_total,
        amort_pct: indicator.amort_pct,
        delta_dc_pct: indicator.delta_dc_pct,
        delta_recompras_pct: indicator.delta_recompras_pct,
        delta_substituicoes_pct: indicator.delta_substituicoes_pct,
        delta_atrasos_pct: indicator.delta_atrasos_pct,
        delta_pdd_pct: indicator.delta_pdd_pct,
        pct_atraso_sobre_dc: indicator.pct_atraso_sobre_dc,
        pct_caixa_sobre_pl: indicator.pct_caixa_sobre_pl,
        pdd_sobre_dc: indicator.pdd_sobre_dc,
        recompra_sobre_dc: indicator.recompra_sobre_dc,
        pct_pdd_sobre_atrasos: indicator.pct_pdd_sobre_atrasos,
        vl_merc_pos_final: indicator.vl_merc_pos_final,
        vl_patrim_liq: indicator.vl_patrim_liq,
        pl_percentage: indicator.pl_percentage,
        ...buildIndicatorDecorations(indicator, isReferenceOnly),
      }
    : buildFallbackIndicatorsRow({
        walletFundId: item.walletFundId,
        operation: item.operation,
        compositionStatus: shouldUseFicComposition
          ? item.compositionStatus
          : undefined,
        isFicChild: shouldUseFicComposition,
        isReferenceOnly,
      });

  if (item.childrenWalletFunds?.length) {
    const children = isFollowedFunds
      ? [...item.childrenWalletFunds].sort(compareFicChildrenByParticipation)
      : item.childrenWalletFunds;

    row.subRows = children.map((child) =>
      buildIndicatorsTableRow(child, true, isFollowedFunds),
    );
  }

  return row;
}
